// src/components/AuthModal.js

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Link,
  Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axiosInstance from '../utils/axiosConfig';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { login } from '../store/authSlice';
import { showSnackbar } from '../store/snackbarSlice';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import shortLogo from '../assets/vester-logo-short.svg';

const BASE_URL = process.env.REACT_APP_BACKEND_URL; 

function AuthModal({ open, onClose, showSignIn = false, onToggleMode }) {
  const dispatch = useDispatch();

  // Toggle sign in vs sign up view in modal
  const [isSignIn, setIsSignIn] = useState(showSignIn);

  // Update isSignIn when showSignIn prop changes
  useEffect(() => {
    setIsSignIn(showSignIn);
  }, [showSignIn]);

  // Height for modal form content and actions
  const contentHeight = isSignIn ? 128 : 310;

  // Basic form data for sign in/up
  const [formData, setFormData] = useState({
    user_email: '',
    user_password: '',
    confirm_password: '',
    user_first_name: '',
    user_last_name: '',
  });

  // controls password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [errors, setErrors] = useState({}); // { fieldName: 'Error message' }
  const [isProcessing, setIsProcessing] = useState(false);

  const [agreeTerms, setAgreeTerms] = useState(false);
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  // Add reset form function
  const resetForm = () => {
    setFormData({
      user_email: '',
      user_password: '',
      confirm_password: '',
      user_first_name: '',
      user_last_name: '',
    });
    setErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
    setAgreeTerms(false);
    setSubscribeNewsletter(false);
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      resetForm();
      setTermsOpen(false);
    }
  }, [open]);

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };
  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' })); // clear error on change
  };


  // Quick email validation
  const validateEmail = (email) => {
    const re = /\S+@\S+\.\S+/;
    return re.test(email.toLowerCase());
  };

  // Add after validateEmail function
  const validatePasswordStrength = (password) => {
    const errors = [];
    
    // Check minimum length
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    // Check for uppercase letter
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    
    // Check for lowercase letter
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    
    // Check for digit
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    
    // Check for allowed special characters
    if (!/[@$!%*?_&]/.test(password)) {
      errors.push('Password must contain at least one special character (@, $, !, %, *, ?, _, &)');
    }
    
    // Check if password contains only allowed characters
    if (!/^[A-Za-z\d@$!%*?&_]*$/.test(password)) {
      errors.push('Password contains invalid characters. Only letters, numbers, and these special characters are allowed: @, $, !, %, *, ?, _, &');
    }
    
    return errors;
  };

  // Helper to sanitize formData by trimming all string fields
  const sanitizeFormData = (data) => {
    const sanitized = {};
    Object.keys(data).forEach((key) => {
      sanitized[key] = typeof data[key] === 'string' ? data[key].trim() : data[key];
    });
    return sanitized;
  };

  // Add new function for resending verification email
  const handleResendVerification = async (email) => {
    setIsProcessing(true);
    try {
      const resp = await axiosInstance.post(
        `/user/resend-verification`,
        { user_email: email },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (resp.status === 200) {
        dispatch(showSnackbar({
          message: 'Verification email has been resent. Please check your inbox.',
          severity: 'success'
        }));
      }
    } catch (err) {
      console.error('Resend verification error:', err);
      dispatch(showSnackbar({
        message: `Failed to resend verification email. ${err.response?.data?.error || 'Please try again.'}`,
        severity: 'error'
      }));
    }
    setIsProcessing(false);
  };

  // Add new function to handle password reset request
  const handleForgotPassword = async () => {
    if (!formData.user_email) {
      dispatch(showSnackbar({
        message: 'Please enter your email address to reset your password',
        severity: 'error'
      }));
      return;
    }

    if (!validateEmail(formData.user_email)) {
      dispatch(showSnackbar({
        message: 'Please enter a valid email address',
        severity: 'error'
      }));
      return;
    }

    setIsProcessing(true);
    try {
      const response = await axiosInstance.post(
        '/user/request-password-reset',
        { user_email: formData.user_email.toLowerCase() }
      );
      
      if (response.status === 200) {
        dispatch(showSnackbar({
          message: 'Password reset link has been sent to your email',
          severity: 'success'
        }));
      }
    } catch (error) {
      console.error('Password reset request error:', error);
      dispatch(showSnackbar({
        message: error.response?.data?.error || 'Failed to send password reset link. Please try again.',
        severity: 'error'
      }));
    }
    setIsProcessing(false);
  };

  // Sign Up
  const handleSignUp = async () => {
    const sanitizedData = sanitizeFormData(formData);
    sanitizedData.user_email = sanitizedData.user_email.toLowerCase();
    const newErrors = validateSignUp();
    
    // Check terms agreement first
    if (!agreeTerms) {
      dispatch(showSnackbar({
        message: 'You must agree to the terms and conditions to create an account',
        severity: 'error'
      }));
      return;
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setIsProcessing(true);

    try {
      const resp = await axiosInstance.post(
        `/user/signup`,
        {
          user_email: sanitizedData.user_email,
          user_password: sanitizedData.user_password,
          user_first_name: sanitizedData.user_first_name,
          user_last_name: sanitizedData.user_last_name,
          user_state: "",
          user_city: "",
          user_zip: "",
          user_street: "",
          user_country: "",
          user_phone: "",
          user_newsletter_subscribed: subscribeNewsletter,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (resp.status === 201) {
        dispatch(showSnackbar({
          message: 'Account created! Please check your email to verify your account before signing in.',
          severity: 'success'
        }));
        // Clear form data
        setFormData({
          user_email: '',
          user_password: '',
          confirm_password: '',
          user_first_name: '',
          user_last_name: '',
        });
        setAgreeTerms(false);
        setSubscribeNewsletter(false);
        // Switch to sign in mode after a delay
        setTimeout(() => {
          setIsSignIn(true);
        }, 3000);
      }
    } catch (err) {
      console.error('Sign-up error:', err);
      dispatch(showSnackbar({
        message: `Sign up failed. ${err.response?.data?.error || 'Please try again.'}`,
        severity: 'error'
      }));
    }
    setIsProcessing(false);
  };

  // Sign In
  const handleSignIn = async (e) => {
    // Prevent form submission default behavior
    if (e) {
      e.preventDefault();
    }
    
    const sanitizedData = sanitizeFormData(formData);
    sanitizedData.user_email = sanitizedData.user_email.toLowerCase();
    const newErrors = validateSignIn();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setIsProcessing(true);

    try {
      const resp = await axiosInstance.post(
        `/user/signin`,
        {
          user_email: sanitizedData.user_email,
          user_password: sanitizedData.user_password,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (resp.status === 200 && resp.data.loginResponse) {
        const { token, user, subscription } = resp.data.loginResponse;
        dispatch(login({ token, user, subscription }));
        onClose();
      }
    } catch (err) {
      console.error('Sign-in error:', err);
      // Check if the error is due to unverified email
      const errorMessage = err.response?.data?.error || '';
      if (errorMessage.toLowerCase().includes('not verified')) {
        dispatch(showSnackbar({
          message: 'Your email is not verified yet. Please check your inbox for the verification email.',
          severity: 'error'
        }));
        // Auto-resend verification email
        handleResendVerification(sanitizedData.user_email);
      } else {
        if (errorMessage.toLowerCase().includes('invalid email') || errorMessage.toLowerCase().includes('invalid  password')) {
          dispatch(showSnackbar({
            message: 'Sign in failed. Invalid email or password',
            severity: 'error'
          }));
        } else {
          dispatch(showSnackbar({
            message: `Sign in failed. ${errorMessage || 'Check your credentials.'}`,
            severity: 'error'
          }));
        }
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Switch between sign-in and sign-up
  const toggleMode = () => {
    const newMode = !isSignIn;
    setIsSignIn(newMode);
    onToggleMode?.(newMode); // Notify parent component
    setErrors({});
    setFormData({
      user_email: '',
      user_password: '',
      confirm_password: '',
      user_first_name: '',
      user_last_name: '',
    });
    setAgreeTerms(false);
    setSubscribeNewsletter(false);
  };

  const validateSignUp = () => {
    const newErrors = {};
    if (!validateEmail(formData.user_email)) {
      newErrors.user_email = 'Invalid email format';
    }
    if (!formData.user_email) {
      newErrors.user_email = 'Email is required';
    }
    if (!formData.user_password) {
      newErrors.user_password = 'Password is required';
    } else {
      const passwordErrors = validatePasswordStrength(formData.user_password);
      if (passwordErrors.length > 0) {
        // Show password errors in snackbar for better visibility
        dispatch(showSnackbar({
          message: passwordErrors.join('. '),
          severity: 'error'
        }));
        newErrors.user_password = 'Password does not meet requirements';
      }
    }
    if (!formData.user_first_name) {
      newErrors.user_first_name = 'First name is required';
    }
    if (!formData.user_last_name) {
      newErrors.user_last_name = 'Last name is required';
    }
    if (!formData.confirm_password) {
      newErrors.confirm_password = 'Confirm your password';
    }
    if (
      formData.user_password &&
      formData.confirm_password &&
      formData.user_password !== formData.confirm_password
    ) {
      newErrors.confirm_password = 'Passwords do not match';
    }
    return newErrors;
  };

  // validation (sign in scenario)
  const validateSignIn = () => {
    const newErrors = {};
    if (!validateEmail(formData.user_email)) {
      newErrors.user_email = 'Invalid email format';
    }
    if (!formData.user_email) {
      newErrors.user_email = 'Email is required';
    }
    if (!formData.user_password) {
      newErrors.user_password = 'Password is required';
    }
    return newErrors;
  };

  return (
    <>
    <Dialog 
      open={open} 
      onClose={() => {
        onClose();
        resetForm();
      }} 
      fullWidth 
      maxWidth="xs"
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '24px',
          background: 'rgba(255, 255, 255, 0.80)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(164, 164, 164, 0.6)',
          boxShadow: '0 10px 50px rgba(0, 0, 0, 0.15)',
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '0px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
            height: 'calc(100% - 16px)',
            marginTop: '16px',
            marginBottom: '8px',
          },
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
        },
      }}
    >
      <form noValidate onSubmit={(e) => {
        e.preventDefault();
        if (isSignIn) {
          handleSignIn();
        } else {
          handleSignUp();
        }
      }}>
        {/* Close button */}
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Centered content */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 6 }}>
          {/* Short logo at top */}
          <Box
            component="img"
            src={shortLogo}
            alt="Logo"
            sx={{ width: 'auto', height: 110, mb: 1 }}
          />
          <Typography variant="h5" sx={{ mb: 1, mt: 2, fontWeight: 500 }}>
            {isSignIn ? 'Sign In' : 'Sign Up'}
          </Typography>
        </Box>


        {/* Dialog Content with fixed height */}
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minHeight: contentHeight,
            pb: '0px',
            pt: '10px',
          }}
        >
          {isProcessing ? (
            // Loader remains in a container of fixed height
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <CircularProgress sx={{ color: '#129de8' }} />
            </Box>
          ) : (
            <>
              {/* If sign up: First and Last name in one row */}
              {!isSignIn && (
                <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                  <TextField
                    required
                    label="First Name"
                    name="user_first_name"
                    value={formData.user_first_name}
                    onChange={handleInputChange}
                    error={!!errors.user_first_name}
                    // helperText={errors.user_first_name}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: 'rgba(255, 255, 255, 0.4)',
                      '& fieldset': {
                          borderColor: 'rgba(180, 180, 180, 0.4)',
                      },
                      '&:hover fieldset': {
                          borderColor: 'rgba(180, 180, 180, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                          borderColor: 'rgba(180, 180, 180, 0.4)',
                      },
                      '&.Mui-focused': {
                          boxShadow: 'none', // remove any default glow
                          backgroundColor: 'rgba(255, 255, 255, 0.6)',
                      },
                      },
                      '& input': {
                      color: '#333',
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(0, 0, 0, 0.6)', // default label color
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: 'rgba(0, 0, 0, 0.6)', // keep same color on focus (prevents turning blue)
                      },
                    }}
                  />
                  <TextField
                    required
                    label="Last Name"
                    name="user_last_name"
                    value={formData.user_last_name}
                    onChange={handleInputChange}
                    error={!!errors.user_last_name}
                    // helperText={errors.user_last_name}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: 'rgba(255, 255, 255, 0.4)',
                      '& fieldset': {
                          borderColor: 'rgba(180, 180, 180, 0.4)',
                      },
                      '&:hover fieldset': {
                          borderColor: 'rgba(180, 180, 180, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                          borderColor: 'rgba(180, 180, 180, 0.4)',
                      },
                      '&.Mui-focused': {
                          boxShadow: 'none', // remove any default glow
                          backgroundColor: 'rgba(255, 255, 255, 0.6)',
                      },
                      },
                      '& input': {
                      color: '#333',
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(0, 0, 0, 0.6)', // default label color
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: 'rgba(0, 0, 0, 0.6)', // keep same color on focus (prevents turning blue)
                      },
                    }}
                  />
                </Box>
              )}

              {/* Email */}
              <TextField
                required
                label="Email"
                name="user_email"
                type="email"
                value={formData.user_email}
                onChange={handleInputChange}
                error={!!errors.user_email}
                // helperText={errors.user_email}
                fullWidth
                sx={{ 
                  mt: !isSignIn ? 1 : 0,
                  '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: 'rgba(255, 255, 255, 0.4)',
                      '& fieldset': {
                          borderColor: 'rgba(180, 180, 180, 0.4)',
                      },
                      '&:hover fieldset': {
                          borderColor: 'rgba(180, 180, 180, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                          borderColor: 'rgba(180, 180, 180, 0.4)',
                      },
                      '&.Mui-focused': {
                          boxShadow: 'none', // remove any default glow
                          backgroundColor: 'rgba(255, 255, 255, 0.6)',
                      },
                      },
                      '& input': {
                      color: '#333',
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(0, 0, 0, 0.6)', // default label color
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: 'rgba(0, 0, 0, 0.6)', // keep same color on focus (prevents turning blue)
                      }, 
                
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (isSignIn) {
                      handleSignIn();
                    } else {
                      const passwordField = document.querySelector('input[name="user_password"]');
                      if (passwordField) passwordField.focus();
                    }
                  }
                }}
              />

              {/* Password */}
              <TextField
                required
                label="Password"
                name="user_password"
                type={showPassword ? 'text' : 'password'}
                value={formData.user_password}
                onChange={handleInputChange}
                error={!!errors.user_password}
                helperText={errors.user_password}
                fullWidth
                sx={{ 
                  mt: !isSignIn ? 1 : 2,
                  '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: 'rgba(255, 255, 255, 0.4)',
                      '& fieldset': {
                          borderColor: 'rgba(180, 180, 180, 0.4)',
                      },
                      '&:hover fieldset': {
                          borderColor: 'rgba(180, 180, 180, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                          borderColor: 'rgba(180, 180, 180, 0.4)',
                      },
                      '&.Mui-focused': {
                          boxShadow: 'none', // remove any default glow
                          backgroundColor: 'rgba(255, 255, 255, 0.6)',
                      },
                      },
                      '& input': {
                      color: '#333',
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(0, 0, 0, 0.6)', // default label color
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: 'rgba(0, 0, 0, 0.6)', // keep same color on focus (prevents turning blue)
                      },
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (isSignIn) {
                      handleSignIn();
                    } else {
                      const confirmPasswordField = document.querySelector('input[name="confirm_password"]');
                      if (confirmPasswordField) confirmPasswordField.focus();
                    }
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      { !isSignIn && (
                      <Tooltip
                        title={
                          <Box sx={{ fontSize: '14px', p: 1, }}>
                            Password must be at least 8 characters long and contain:
                            <br />
                            <br />• At least one uppercase letter
                            <br />
                            <br />• At least one lowercase letter  
                            <br />
                            <br />• At least one number
                            <br />
                            <br />• At least one special character: @, $, !, %, *, ?, _, &
                          </Box>
                        }
                        arrow
                        placement="top"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              maxWidth: '500px',
                            },
                          },
                        }}
                      >
                        <IconButton edge="end" tabIndex={-1}>
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      )}
                      <IconButton onClick={handleTogglePassword} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </Box>
                  </InputAdornment>
                  ),
                }}
              />

              {/* Add Forgot Password link for sign in mode */}
              {isSignIn && (
                <Box sx={{ width: '100%', mt: 1 }}>
                  <Button
                    onClick={handleForgotPassword}
                    sx={{
                      textTransform: 'none',
                      p: 0,
                      color: '#129de8',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Forgot password?
                  </Button>
                </Box>
              )}

              {/* Confirm Password for sign up */}
              {!isSignIn && (
                <TextField
                  required
                  label="Confirm Password"
                  name="confirm_password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirm_password}
                  onChange={handleInputChange}
                  error={!!errors.confirm_password}
                  // helperText={errors.confirm_password}
                  fullWidth
                  sx={{ 
                    mt: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: 'rgba(255, 255, 255, 0.4)',
                      '& fieldset': {
                          borderColor: 'rgba(180, 180, 180, 0.4)',
                      },
                      '&:hover fieldset': {
                          borderColor: 'rgba(180, 180, 180, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                          borderColor: 'rgba(180, 180, 180, 0.4)',
                      },
                      '&.Mui-focused': {
                          boxShadow: 'none', // remove any default glow
                          backgroundColor: 'rgba(255, 255, 255, 0.6)',
                      },
                      },
                      '& input': {
                      color: '#333',
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(0, 0, 0, 0.6)', // default label color
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: 'rgba(0, 0, 0, 0.6)', // keep same color on focus (prevents turning blue)
                      },
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSignUp();
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleToggleConfirmPassword} edge="end">
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              {/* Beta disclaimer for sign up */}
              {!isSignIn && (
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(0, 0, 0, 0.6)',
                    }}
                  >
                    This product is in beta and for experimental use only.
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(0, 0, 0, 0.6)',
                      mt: 0
                    }}
                  >
                    None of the content provided is financial advice.
                  </Typography>
                </Box>
              )}

              {/* Terms and Conditions */}
              {!isSignIn && (
                <Box sx={{ 
                  width: '100%', 
                  mt: 1, 
                  mb: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={agreeTerms}
                        onChange={(e) => setAgreeTerms(e.target.checked)}
                        name="agreeTerms"
                        sx={{ p: 0, pr: 1, mt: '-2px', ml: '20px' }}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I agree to the{' '}
                        <Button
                          type="button"
                          onClick={() => setTermsOpen(true)}
                          sx={{
                            textTransform: 'none',
                            p: 0,
                            pb: '2px',
                            color: '#129de8',
                            '&:hover': { textDecoration: 'underline', backgroundColor: 'transparent' },
                          }}
                        >
                          terms and conditions
                        </Button>
                      </Typography>
                    }
                  />
                  
                  {/* Newsletter Subscription */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={subscribeNewsletter}
                        onChange={(e) => setSubscribeNewsletter(e.target.checked)}
                        name="subscribeNewsletter"
                        sx={{ p: 0, pr: 1, mt: '-2px', ml: '-5px' }}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Subscribe to Vester Newsletter
                      </Typography>
                    }
                    sx={{ mt: 1 }}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>

        {/* Dialog Actions, only shown when not processing */}
        {!isProcessing && (
          <DialogActions sx={{ flexDirection: 'column', mb: 2, mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              sx={{
                backgroundColor: '#129de8',
                color: '#fff',
                borderRadius: '12px',
                textTransform: 'none',
                width: '50%',
                fontSize: '14px',
                '&:hover': {
                  backgroundColor: '#0c8ecf',
                },
              }}
            >
              {isSignIn ? 'Sign In' : 'Sign Up'}
            </Button>
            <Box sx={{ mt: 1.75 }}>
              {isSignIn ? (
                <Typography variant="body2">
                  Don't have an account?{' '}
                  <Button 
                    onClick={toggleMode} 
                    sx={{ textTransform: 'none', p: 0, ml: '4px', color: '#129de8' }}
                    type="button"
                  >
                    Sign Up
                  </Button>
                </Typography>
              ) : (
                <Typography variant="body2">
                  Already have an account?{' '}
                  <Button 
                    onClick={toggleMode} 
                    sx={{ textTransform: 'none', p: 0, ml: '4px', color: '#129de8' }}
                    type="button"
                  >
                    Sign In
                  </Button>
                </Typography>
              )}
            </Box>
          </DialogActions>
        )}
      </form>
    </Dialog>

    <Dialog open={termsOpen} onClose={() => setTermsOpen(false)} fullWidth maxWidth="md">
      <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
        <IconButton onClick={() => setTermsOpen(false)}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ 
        mt: 4, 
        maxHeight: '80vh', 
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none'
      }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Terms & Conditions
        </Typography>
        <Typography variant="body2" paragraph>
          Welcome, and thank you for your interest in Vester — accessible via{' '}
          <Link href="https://vesterai.com" target="_blank" rel="noopener noreferrer" sx={{ color: '#129de8', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            vesterai.com
          </Link>
          , dashboard.vesterai.com 
          (together, the "Website" or "Site"), and the Vester mobile application (the "Application"), owned and 
          operated by Vester Technologies, Inc. ("Vester", "we", "our", or "us"). Unless otherwise specified, all 
          references to our services ("Service" or "Services") include the Application, Website, and any content, 
          tools, APIs, analytics, and software provided to enable access to or use of the Services. The term "you" 
          refers to all users of our Services, including casual visitors and registered users.
        </Typography>
        <Typography variant="body2" paragraph>
          These Terms of Service, including the AI Risk Disclaimer (collectively, the "Terms"), form a legally 
          binding agreement between you and Vester regarding your access to and use of the Services.
        </Typography>
        <Typography variant="body2" paragraph>
          By accessing or using any part of the Services, you affirm that you have read, understood, and agreed 
          to be bound by these Terms and our Privacy Policy. If you do not agree, you must not access or use any of our Services.
        </Typography>

        <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
          Risk Disclaimer
        </Typography>
        <Typography variant="body2" paragraph>
        Vester is an educational and research platform offering informational tools for understanding cryptocurrency and digital assets. We do not and cannot provide personalized financial, investment, legal, or tax advice. The Services are not intended to be relied upon for the purpose of making investment decisions.
        </Typography>
        <Typography variant="body2" paragraph>
        We are not registered or regulated by the SEC, FINRA, CFTC, or any other financial services authority. No communication from Vester should be interpreted as a solicitation or offer to buy or sell securities or other financial instruments.
        </Typography>
        <Typography variant="body2" paragraph>
        Your investment decisions are your sole responsibility. You should consult a licensed financial advisor before acting on any information found on the Service. You acknowledge that digital asset markets are volatile and carry risk, including the complete loss of principal.
        </Typography>

        <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
        Modifications to Terms
        </Typography>
        <Typography variant="body2" paragraph>
        Vester reserves the right to update or revise these Terms at any time. Revisions are effective immediately upon posting to the Website or Application. You are responsible for regularly reviewing the Terms. Continued use after a change constitutes acceptance. We may notify you of changes via email or app notification but are not required to do so.
        </Typography>
        <Typography variant="body2" paragraph>
        Any updates to dispute resolution provisions will not apply retroactively to disputes of which the parties had actual notice prior to the effective date of the change.
        </Typography>

        <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
          Eligibility
        </Typography>
        <Typography variant="body2" paragraph>
        To use our Services, you must:
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Be at least 18 years old
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Have full legal capacity to enter into contracts
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Not be barred from using the Services under applicable laws or regulations
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Not be on any U.S. or international government list of prohibited or restricted parties
          </Typography>
        </Box>
        <Typography variant="body2" paragraph>
        Vester reserves the right to request proof of eligibility at any time and may suspend or terminate access for noncompliance.
        </Typography>

        <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
          Accounts and Security
        </Typography>
        <Typography variant="body2" paragraph>
        You may be required to create an account to access certain Services. You agree to:
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Provide accurate and current registration information
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Maintain the confidentiality of your login credentials
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Accept full responsibility for all activities under your account
          </Typography>
        </Box>
        <Typography variant="body2" paragraph>
        You must notify Vester immediately of any unauthorized access. We reserve the right to suspend or terminate accounts if suspicious activity is detected or these Terms are violated.
        </Typography>

        <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
          Privacy
        </Typography>
        <Typography variant="body2" paragraph>
          Your use of the Services is subject to our Privacy Policy, which governs how we collect, store, and use your personal information.
        </Typography>

        <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
          License and Acceptable Use
        </Typography>
        <Typography variant="body2" paragraph>
          Vester grants you a limited, non-exclusive, non-transferable, revocable license to use the Services for personal, non-commercial use only.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          You may not:
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Reproduce, sell, lease, or resell the Services
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Use the Services for unauthorized commercial purposes
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Use the Services to create or train AI models
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Bypass or circumvent security measures
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Use scraping, spiders, or bots to extract data
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Attempt to reverse-engineer or exploit any software or algorithms
          </Typography>
        </Box>
        <Typography variant="body2" paragraph>
          Vester reserves all rights not expressly granted in these Terms.
        </Typography>

        <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
          Subscription Terms
        </Typography>
        <Typography variant="body2" paragraph>
          Some features are offered on a subscription basis. By subscribing:
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            You authorize Vester to charge your payment method on a recurring basis
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            You agree to our then-current pricing and billing schedule
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Subscriptions automatically renew unless canceled before the renewal date
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            All payments are non-refundable except where required by law
          </Typography>
        </Box>
        <Typography variant="body2" paragraph>
          Failure to pay may result in suspension or termination of your access. You may cancel your subscription via your account settings or by contacting support.
        </Typography>

        <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
          Intellectual Property
        </Typography>
        <Typography variant="body2" paragraph>
          All content, features, design elements, software, data, models, charts, and trademarks associated with Vester are the intellectual property of Vester Inc or its licensors.
        </Typography>
        <Typography variant="body2" paragraph>
          You agree not to use, reproduce, distribute, or modify Vester's proprietary assets without express written consent.
        </Typography>

        <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
          User Content
        </Typography>
        <Typography variant="body2" paragraph>
          If you submit content (charts, prompts, feedback, etc.), you:
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Grant Vester a worldwide, irrevocable, royalty-free license to use, adapt, publish, and display your content
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Represent that you own or have the necessary rights to the content
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Acknowledge that content submitted in public areas may be visible to others
          </Typography>
        </Box>
        <Typography variant="body2" paragraph>
          You may not post illegal, defamatory, or harmful content. Vester reserves the right to remove content or suspend access for violations.
        </Typography>

        <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
          Third-Party Integrations
        </Typography>
        <Typography variant="body2" paragraph>
          Vester may link or integrate with third-party tools (e.g., data providers, exchanges). These third parties are governed by their own terms and privacy policies. Vester does not endorse or control these services and is not responsible for their availability, accuracy, or reliability.
        </Typography>

        <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
          AI Model Use Restriction
        </Typography>
        <Typography variant="body2" paragraph>
          You may not use Vester's Services, content, or outputs to develop, train, fine-tune, validate, or improve any artificial intelligence or machine learning models or datasets. This includes, but is not limited to, generative AI systems, LLMs, and financial modeling agents.
        </Typography>

        <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
          Disclaimers and No Warranties
        </Typography>
        <Typography variant="body2" paragraph>
          Services are provided "AS IS" without warranties of any kind. Vester disclaims all warranties, express or implied, including accuracy, reliability, merchantability, and fitness for a particular purpose.
        </Typography>
        <Typography variant="body2" paragraph>
          We do not guarantee uninterrupted access, error-free functionality, or that any outputs from our models or analytics are suitable for any specific use case.
        </Typography>

        <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
          Limitation of Liability
        </Typography>
        <Typography variant="body2" paragraph>
          To the maximum extent permitted by law:
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Vester is not liable for indirect, incidental, consequential, or punitive damages
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Vester's total liability for any claim will not exceed $100
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            You use the Service at your own risk
          </Typography>
        </Box>
        <Typography variant="body2" paragraph>
          Certain jurisdictions do not allow limitations, so these exclusions may not apply to you.
        </Typography>

        <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
          Indemnification
        </Typography>
        <Typography variant="body2" paragraph>
          You agree to indemnify and hold harmless Vester, its affiliates, officers, and employees from any claims, damages, liabilities, or costs arising from:
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Your use of the Services
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Your violation of these Terms
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Your submitted content
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Any harm caused to others through your use
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
          Termination
        </Typography>
        <Typography variant="body2" paragraph>
          Vester may terminate or suspend access to the Services at any time for violations of these Terms, suspected fraud, legal compliance issues, or technical reasons.
        </Typography>
        <Typography variant="body2" paragraph>
          Upon termination:
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            All rights granted to you will cease
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            Subscription payments are non-refundable
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'text.primary', mr: 1, display: 'inline-block' }} />
            You must cease all use of the Services and delete any downloaded content
          </Typography>
        </Box>

        <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
          Governing Law
        </Typography>
        <Typography variant="body2" paragraph>
          These Terms are governed by the laws of the State of Delaware, without regard to conflict of laws. Any legal action must be brought in Delaware state or federal courts.
        </Typography>

        <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
          Arbitration and Class Action Waiver
        </Typography>
        <Typography variant="body2" paragraph>
          Disputes must be resolved via binding arbitration per the AAA rules. You waive any right to a jury trial or class action. You agree to individual arbitration only, with no consolidated or representative claims.
        </Typography>

        <Typography variant="h5" sx={{ mb: 2, mt: 4, fontWeight: 600 }}>
          Contact
        </Typography>
        <Typography variant="body2" paragraph>
          For questions about these Terms or the Services: Email:{' '}
          <Link 
            href="mailto:grant@vesterai.com"
            sx={{ 
              color: '#129de8', 
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            grant@vesterai.com
          </Link>
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4, pb: 2 }}>
          <Button
            onClick={() => {
              setAgreeTerms(true);
              setTermsOpen(false);
            }}
            variant="contained"
            sx={{
              backgroundColor: '#129de8',
              color: '#fff',
              borderRadius: '20px',
              textTransform: 'none',
              width: '120px',
              '&:hover': {
                backgroundColor: '#0c8ecf',
              },
            }}
          >
            Got It
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
    </>
  );
}

export default AuthModal;
